import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  makeStyles,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
} from "@material-ui/core";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";

import { 
  Help as HelpIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  VideoLibrary as VideoIcon,
  Title as TitleIcon,
  Description as DescriptionIcon,
  PlayCircleOutline as PlayIcon,
  YouTube as YouTubeIcon,
  Movie as MovieIcon
} from "@material-ui/icons";

import useHelps from "../../hooks/useHelps";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../ConfirmationModal";

/**
 * Valida si una URL es válida y accesible
 * @param {string} url - URL a validar
 * @returns {boolean} - true si la URL es válida
 */
const isValidUrl = (url) => {
  try {
    const urlObj = new URL(url);
    // Solo permitir http y https
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch (e) {
    return false;
  }
};

/**
 * Detecta el tipo de video (YouTube o externo) y retorna el tipo y valor procesado
 * @param {string} input - URL o código de video
 * @returns {{type: 'youtube'|'external', value: string, error?: string}|null}
 */
const detectVideoType = (input) => {
  if (!input || typeof input !== 'string') return null;
  
  const trimmedInput = input.trim();
  if (!trimmedInput) return null;
  
  // Patrones para detectar YouTube
  const youtubePatterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/
  ];
  
  // Verificar si es URL de YouTube
  for (const pattern of youtubePatterns) {
    const match = trimmedInput.match(pattern);
    if (match && match[1]) {
      return {
        type: 'youtube',
        value: match[1] // Código de 11 caracteres
      };
    }
  }
  
  // Si es exactamente 11 caracteres alfanuméricos, asumir código de YouTube
  if (trimmedInput.length === 11 && /^[a-zA-Z0-9_-]{11}$/.test(trimmedInput)) {
    return {
      type: 'youtube',
      value: trimmedInput
    };
  }
  
  // Verificar si es una URL externa (http/https)
  const urlPattern = /^https?:\/\/.+/i;
  if (urlPattern.test(trimmedInput)) {
    // Validar formato de URL
    if (!isValidUrl(trimmedInput)) {
      return {
        type: 'external',
        value: null,
        error: i18n.t("helps.validation.urlInvalid"),
      };
    }
    
    // Verificar si tiene extensión de video (recomendado pero no obligatorio)
    const videoExtensions = /\.(mp4|webm|ogg|ogv|mov|avi|wmv|flv|mkv)(\?.*)?$/i;
    const hasVideoExtension = videoExtensions.test(trimmedInput);
    
    // Es URL externa
    return {
      type: 'external',
      value: encodeURI(trimmedInput), // Codificar URL para manejar espacios y caracteres especiales
      warning: !hasVideoExtension ? i18n.t("helps.validation.urlExtensionHint") : null
    };
  }
  
  // Si no coincide con ningún patrón, retornar null
  return null;
};

/**
 * Procesa el input del usuario y retorna el valor procesado según el tipo de video
 * @param {string} input - URL o código de video ingresado por el usuario
 * @returns {string|null} - Valor procesado para guardar en BD, o null si no es válido
 */
const processVideoInput = (input) => {
  const detected = detectVideoType(input);
  
  if (!detected) {
    return null;
  }
  
  return detected.value;
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(2),
  },
  // ✅ CORREGIDO: FormCard dinámico según tema
  formCard: {
    borderRadius: theme.spacing(1),
    boxShadow: theme.mode === 'dark' ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
    marginBottom: theme.spacing(3),
  },
  formCardContent: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  },
  formTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.primary.main,
    textAlign: "center",
    justifyContent: "center",
  },
  formGrid: {
    marginTop: theme.spacing(1),
  },
  fieldContainer: {
    marginBottom: theme.spacing(1),
  },
  // ✅ CORREGIDO: TextField dinámico según tema
  textField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      borderRadius: theme.spacing(1),
      color: theme.palette.text.primary,
      "& fieldset": {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&:hover": {
        backgroundColor: theme.mode === 'dark' ? '#4B5563' : "#fafafa",
      },
      "&.Mui-focused": {
        backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
      fontWeight: "500",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
  buttonContainer: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "center",
    marginTop: theme.spacing(2),
    flexWrap: "wrap",
  },
  actionButton: {
    borderRadius: theme.spacing(1),
    textTransform: "none",
    fontWeight: "600",
    padding: theme.spacing(1, 2),
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
    "&:hover": {
      boxShadow: "0 2px 6px rgba(0,0,0,0.16)",
    },
    transition: "all 0.2s ease",
    minWidth: 100,
  },
  primaryButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  secondaryButton: {
    backgroundColor: theme.palette.grey[500],
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.grey[600],
    },
  },
  errorButton: {
    backgroundColor: theme.palette.error.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
  sectionTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  // ✅ CORREGIDO: TableCard dinámico según tema
  tableCard: {
    borderRadius: theme.spacing(1),
    boxShadow: theme.mode === 'dark' ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
    overflow: "auto",
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "& .MuiTableCell-head": {
      color: "white",
      fontWeight: "bold",
    },
  },
  // ✅ CORREGIDO: TableRow dinámico según tema
  tableRow: {
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.action.hover,
    },
    cursor: "pointer",
  },
  // ✅ CORREGIDO: TableCell dinámico según tema
  tableCell: {
    padding: theme.spacing(1),
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.mode === 'dark' ? '#4B5563' : 'rgba(224, 224, 224, 0.5)'}`,
  },
  editButton: {
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.mode === 'dark' ? 'rgba(59, 130, 246, 0.2)' : theme.palette.primary.light,
      color: theme.mode === 'dark' ? theme.palette.primary.light : "white",
    },
  },
  videoChip: {
    backgroundColor: "#ff4444",
    color: "white",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  helperText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
}));

export function HelpFormImproved(props) {
  const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
  const classes = useStyles();

  const [record, setRecord] = useState({
    title: '',
    description: '',
    video: ''
  });

  const [detectedVideoType, setDetectedVideoType] = useState(null); // 'youtube', 'external', o null

  useEffect(() => {
    setRecord(initialValue);
    // Detectar tipo del video inicial si existe
    if (initialValue?.video) {
      const detected = detectVideoType(initialValue.video);
      setDetectedVideoType(detected ? detected.type : null);
    } else {
      setDetectedVideoType(null);
    }
  }, [initialValue]);

  const handleSubmit = async (data) => {
    // Validar que haya un video ingresado
    if (!data.video || !data.video.trim()) {
      toast.error('Por favor ingresa una URL de YouTube o una URL externa de video');
      return;
    }
    
    // Si es una actualización y el video no cambió, usar el valor original sin procesar
    // Esto evita doble codificación de URLs ya codificadas
    const videoChanged = !record.id || data.video !== record.video;
    
    if (!videoChanged) {
      // El video no cambió, usar el valor original (ya codificado de BD)
      // No procesar para evitar doble codificación
      onSubmit(data);
      return;
    }
    
    // El video cambió, procesar normalmente
    // Primero decodificar si ya está codificado para evitar doble codificación
    let videoToProcess = data.video.trim();
    try {
      // Intentar decodificar si parece estar codificado
      if (videoToProcess.includes('%')) {
        const decoded = decodeURIComponent(videoToProcess);
        // Si la decodificación fue exitosa y es diferente, usar la decodificada
        if (decoded !== videoToProcess && isValidUrl(decoded)) {
          videoToProcess = decoded;
        }
      }
    } catch (e) {
      // Si falla la decodificación, usar el valor original
      // (puede ser que no esté codificado o tenga formato especial)
    }
    
    // Detectar tipo y validar
    const detected = detectVideoType(videoToProcess);
    
    if (!detected) {
      toast.error(i18n.t("helps.validation.invalidVideoInput"));
      return;
    }
    
    // Verificar si hay error en la detección
    if (detected.error) {
      toast.error(detected.error);
      return;
    }
    
    // Procesar el input del video
    const processedVideo = processVideoInput(videoToProcess);
    
    if (!processedVideo) {
      toast.error(i18n.t("helps.validation.processFailed"));
      return;
    }
    
    // Mostrar advertencia si existe
    if (detected.warning) {
      toast.warning(detected.warning, { autoClose: 5000 });
    }
    
    // Asignar el video procesado
    data.video = processedVideo;
    
    // Mostrar mensaje de éxito según tipo
    if (detected.type === 'youtube') {
      toast.success(i18n.t("helps.toast.youtubeDetected", { code: processedVideo }));
    } else if (detected.type === 'external') {
      toast.success(i18n.t("helps.toast.externalDetected"));
    }
    
    onSubmit(data);
  };

  return (
    <Card className={classes.formCard}>
      <CardContent className={classes.formCardContent}>
        <Typography variant="h5" className={classes.formTitle}>
          <HelpIcon />
          {record.id ? i18n.t("helps.formTitle.edit") : i18n.t("helps.formTitle.create")}
        </Typography>
        
        <Formik
          enableReinitialize
          initialValues={record}
          onSubmit={(values, { resetForm }) =>
            setTimeout(() => {
              handleSubmit(values);
              resetForm();
            }, 500)
          }
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Grid container spacing={2} className={classes.formGrid}>
                {/* Información del Video */}
                <Grid item xs={12}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    <VideoIcon />
                    {i18n.t("helps.sectionVideo")}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <div className={classes.fieldContainer}>
                    <TextField
                      label={i18n.t("helps.form.title")}
                      name="title"
                      value={values.title || ""}
                      onChange={(e) => setFieldValue('title', e.target.value)}
                      variant="outlined"
                      fullWidth
                      className={classes.textField}
                      InputProps={{
                        startAdornment: <TitleIcon style={{ marginRight: 8, color: "#666" }} />
                      }}
                    />
                  </div>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <div className={classes.fieldContainer}>
                    <TextField
                      label={i18n.t("helps.form.videoCode")}
                      name="video"
                      value={values.video || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFieldValue('video', value);
                        // Detectar tipo en tiempo real
                        const detected = detectVideoType(value);
                        setDetectedVideoType(detected ? detected.type : null);
                      }}
                      variant="outlined"
                      fullWidth
                      className={classes.textField}
                      placeholder={i18n.t("helps.placeholders.video")}
                      InputProps={{
                        startAdornment: detectedVideoType === 'external' 
                          ? <MovieIcon style={{ marginRight: 8, color: "#666" }} />
                          : <YouTubeIcon style={{ marginRight: 8, color: "#666" }} />
                      }}
                    />
                    <Typography className={classes.helperText}>
                      {detectedVideoType === 'youtube' 
                        ? i18n.t("helps.helpers.youtubeDetected")
                        : detectedVideoType === 'external'
                        ? i18n.t("helps.helpers.externalDetected")
                        : i18n.t("helps.helpers.pasteHint")}
                    </Typography>
                  </div>
                </Grid>
                
                <Grid item xs={12}>
                  <div className={classes.fieldContainer}>
                    <TextField
                      label={i18n.t("helps.form.description")}
                      name="description"
                      value={values.description || ""}
                      onChange={(e) => setFieldValue('description', e.target.value)}
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={3}
                      className={classes.textField}
                      InputProps={{
                        startAdornment: <DescriptionIcon style={{ marginRight: 8, color: "#666", alignSelf: "flex-start", marginTop: 8 }} />
                      }}
                    />
                  </div>
                </Grid>
                
                <Grid item xs={12}>
                  <div className={classes.fieldContainer}>
                    <TextField
                      label={i18n.t("helps.thumbnail.label")}
                      name="thumbnail"
                      value={values.thumbnail || ""}
                      onChange={(e) => setFieldValue('thumbnail', e.target.value)}
                      variant="outlined"
                      fullWidth
                      className={classes.textField}
                      placeholder={i18n.t("helps.placeholders.thumbnail")}
                      InputProps={{
                        startAdornment: <VideoIcon style={{ marginRight: 8, color: "#666" }} />
                      }}
                    />
                    <Typography className={classes.helperText}>
                      {i18n.t("helps.thumbnail.hint")}
                    </Typography>
                  </div>
                </Grid>
              </Grid>

              {/* Botones de Acción */}
              <div className={classes.buttonContainer}>
                <Button
                  variant="contained"
                  className={`${classes.actionButton} ${classes.secondaryButton}`}
                  onClick={onCancel}
                  startIcon={<ClearIcon />}
                  disabled={loading}
                >
                  {i18n.t("helps.buttons.clear")}
                </Button>
                
                {record.id !== undefined && (
                  <Button
                    variant="contained"
                    className={`${classes.actionButton} ${classes.errorButton}`}
                    onClick={() => onDelete(record)}
                    startIcon={<DeleteIcon />}
                    disabled={loading}
                  >
                    {i18n.t("helps.buttons.delete")}
                  </Button>
                )}
                
                <Button
                  type="submit"
                  variant="contained"
                  className={`${classes.actionButton} ${classes.primaryButton}`}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                >
                  {loading ? i18n.t("helps.buttons.saving") : i18n.t("helps.buttons.save")}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </CardContent>
    </Card>
  );
}

export function HelpsManagerGridImproved(props) {
  const { records, onSelect } = props;
  const classes = useStyles();

  const renderVideoCode = (videoCode) => {
    if (!videoCode) return '-';
    
    return (
      <Chip
        label={videoCode}
        size="small"
        className={classes.videoChip}
        icon={<PlayIcon />}
      />
    );
  };

  if (records.length === 0) {
    return (
      <Card className={classes.tableCard}>
        <CardContent className={classes.emptyState}>
          <Typography variant="h6" color="textSecondary">
            {i18n.t("helps.grid.emptyTitle")}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {i18n.t("helps.grid.emptySubtitle")}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.tableCard}>
      <Table>
        <TableHead className={classes.tableHeader}>
          <TableRow>
            <TableCell align="center" style={{ width: "60px" }}>#</TableCell>
            <TableCell align="left">
              <TitleIcon style={{ marginRight: 8 }} />
              {i18n.t("helps.table.title")}
            </TableCell>
            <TableCell align="left">
              <DescriptionIcon style={{ marginRight: 8 }} />
              {i18n.t("helps.table.description")}
            </TableCell>
            <TableCell align="center">
              <VideoIcon style={{ marginRight: 8 }} />
              {i18n.t("helps.table.video")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((row, key) => (
            <TableRow 
              className={classes.tableRow} 
              key={key}
              onClick={() => onSelect(row)}
            >
              <TableCell align="center" className={classes.tableCell}>
                <Tooltip title={i18n.t("helps.tooltips.editRow")}>
                  <IconButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(row);
                    }} 
                    className={classes.editButton}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
              <TableCell align="left" className={classes.tableCell}>
                <Typography variant="body2" style={{ fontWeight: "bold" }}>
                  {row.title || '-'}
                </Typography>
              </TableCell>
              <TableCell align="left" className={classes.tableCell}>
                <Typography variant="body2">
                  {row.description || '-'}
                </Typography>
              </TableCell>
              <TableCell align="center" className={classes.tableCell}>
                {renderVideoCode(row.video)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

export default function HelpsManagerImproved() {
  const classes = useStyles();
  const { list, save, update, remove } = useHelps();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState({
    title: '',
    description: '',
    video: ''
  });

  useEffect(() => {
    loadHelps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHelps = async () => {
    setLoading(true);
    try {
      const helpList = await list();
      setRecords(helpList);
    } catch (e) {
      toast.error(i18n.t("helps.messages.loadError"));
    }
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadHelps();
      handleCancel();
      toast.success(i18n.t("helps.messages.saveSuccess"));
    } catch (e) {
      toast.error(i18n.t("helps.messages.saveError"));
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      await loadHelps();
      handleCancel();
      toast.success(i18n.t("helps.messages.deleteSuccess"));
    } catch (e) {
      toast.error(i18n.t("helps.messages.deleteError"));
    }
    setLoading(false);
  };

  const handleOpenDeleteDialog = () => {
    setShowConfirmDialog(true);
  };

  const handleCancel = () => {
    setRecord({
      title: '',
      description: '',
      video: '',
      thumbnail: ''
    });
  };

  const handleSelect = (data) => {
    setRecord({
      id: data.id,
      title: data.title || '',
      description: data.description || '',
      video: data.video || '',
      thumbnail: data.thumbnail || ''
    });
  };

  return (
    <div className={classes.root}>
      <HelpFormImproved
        initialValue={record}
        onDelete={handleOpenDeleteDialog}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        loading={loading}
      />
      
      <HelpsManagerGridImproved
        records={records}
        onSelect={handleSelect}
      />
      
      <ConfirmationModal
        title={i18n.t("helps.messages.deleteTitle")}
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleDelete()}
      >
        {i18n.t("helps.messages.deleteMessage")}
      </ConfirmationModal>
    </div>
  );
}
